import { Modal } from 'src/components/Modal';
import {
  NEW_PLANS_BUSINESS_DETAILS,
  NEW_PLANS_DETAILS,
  PLANS_DETAILS,
  PlansDetailsDataType,
  SUPER_PROJECT_NAME,
} from 'src/constants';
import styles from './RequestUsageTable.module.scss';

import {
  interfaceControlState,
  setIsShowRequestUsageTable,
  useAppDispatch,
  useAppSelector,
} from 'src/store';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useEntitlements, usePaymentControlState } from 'src/hooks';
import { UserTier } from 'src/types/wallet/models/UserTier';
import { CanonicalInterval } from 'src/types';

export const plans = ['Pro', 'Ultra', 'Max'];
export const plansTiersIndividual = [
  UserTier.SUPERNINJA_STANDARD,
  UserTier.SUPERNINJA_PRO,
  UserTier.SUPERNINJA_ULTRA,
  UserTier.SUPERNINJA_MAX,
];

export const plansTiersBusiness = [
  UserTier.BUSINESS_SUPERNINJA_PRO,
  UserTier.BUSINESS_SUPERNINJA_ULTRA,
  UserTier.BUSINESS_SUPERNINJA_MAX,
];

const columnClasses = [styles.lightGray, styles.gray, styles.lightGray];

type RequestUsageTableProps = {
  isNewVariant: boolean;
};

export const RequestUsageTable = ({ isNewVariant }: RequestUsageTableProps) => {
  const dispatch = useAppDispatch();
  const { selectedPeriod } = usePaymentControlState();

  const { isShowRequestUsageTable, usageTableType } = useAppSelector(
    interfaceControlState,
  );

  // todo switch it temporary ON
  const isEnabledStandardPlan = true;
  const isEnabledUltraPlan = true;

  const isBusiness = usageTableType === 'business';

  const { entitlements } = useEntitlements({
    isBusiness,
    interval: selectedPeriod,
  });

  const newPlans = useMemo(() => {
    const plansResult = [];
    if (isEnabledStandardPlan && !isBusiness) {
      plansResult.push('Standard');
    }
    plansResult.push('Pro');
    if (isEnabledUltraPlan) {
      plansResult.push('Ultra');
    }
    plansResult.push('Max');
    return plansResult;
  }, [isEnabledStandardPlan, isEnabledUltraPlan, isBusiness]);

  const getPlanWithValues = useCallback(
    (planDetails: PlansDetailsDataType[]) => {
      return planDetails.map((item) => {
        const values = (
          isBusiness ? plansTiersBusiness : plansTiersIndividual
        ).map((tier) => {
          const findedValue = entitlements.find((ent) => ent.tier === tier);
          if (!!findedValue) {
            return findedValue.limits[item.fieldName].value.toString();
          }
          return tier;
        });
        return { ...item, values: values };
      });
    },
    [entitlements, isBusiness],
  );

  const plansData = useMemo(() => {
    return isNewVariant
      ? isBusiness
        ? getPlanWithValues(NEW_PLANS_BUSINESS_DETAILS)
        : getPlanWithValues(NEW_PLANS_DETAILS)
      : (PLANS_DETAILS as PlansDetailsDataType[]);
  }, [isNewVariant, isBusiness, getPlanWithValues]);

  const handleClose = () => {
    dispatch(setIsShowRequestUsageTable());
  };

  return (
    <Modal
      open={isShowRequestUsageTable}
      onClose={handleClose}
      className={styles.modal}
      blockScroll={false}
    >
      <div>
        <p className={styles.title}>{SUPER_PROJECT_NAME} tasks</p>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.header}>Plans</th>

                {(isNewVariant ? newPlans : plans).map((plan, index) => (
                  <th
                    key={plan}
                    className={classNames(styles.header, columnClasses[index])}
                  >
                    {plan}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(plansData || []).map(
                ({ type, description, values, bottomComponent }, index) => (
                  <tr key={type} className={styles.row}>
                    <td className={styles.cell}>
                      <div className={styles.label}>
                        {type}{' '}
                        {index === 0
                          ? selectedPeriod === CanonicalInterval.ANNUALLY
                            ? '(per year)'
                            : ''
                          : ''}
                      </div>

                      <div className={styles.description}>{description}</div>

                      {bottomComponent ? bottomComponent : null}
                    </td>

                    {values.map((value, index) => (
                      <td
                        key={index}
                        className={classNames(
                          styles.cell,
                          columnClasses[index],
                        )}
                      >
                        {value.replace('-1', 'Unlimited')}
                      </td>
                    ))}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};
